const clamp = (value, min, max, fallback) => Math.min(max, Math.max(min, Number(value) || fallback));

export function arrangement({ count = 2, width = 24, height = 32, gap = 3, layout = 'row', sofaWidth = 84 }) {
  count = Math.round(clamp(count, 1, 4, 2));
  width = clamp(width, 1, 60, 24);
  height = clamp(height, 1, 80, 32);
  gap = clamp(gap, 1, 8, 3);
  sofaWidth = clamp(sofaWidth, 60, 120, 84);
  const columns = count === 4 && layout === 'grid' ? 2 : count;
  const rows = Math.ceil(count / columns);
  const spanWidth = columns * width + (columns - 1) * gap;
  const spanHeight = rows * height + (rows - 1) * gap;
  const sofaLeft = 508;
  const sofaRight = 937;
  const sofaTop = 769;
  const centre = (sofaLeft + sofaRight) / 2;
  const scale = (sofaRight - sofaLeft) / sofaWidth;
  const left = centre - spanWidth * scale / 2;
  const top = sofaTop - (8 + spanHeight) * scale;
  const positions = Array.from({ length: count }, (_, index) => ({
    x: left + (index % columns) * (width + gap) * scale,
    y: top + Math.floor(index / columns) * (height + gap) * scale,
    width: width * scale,
    height: height * scale
  }));
  const padding = 39;
  const bottom = 1014;
  const viewWidth = Math.max(815, spanWidth * scale + padding * 2, (bottom - top + padding) * 4 / 3);
  const viewHeight = viewWidth * 3 / 4;
  const viewBox = [centre - viewWidth / 2, bottom - viewHeight, viewWidth, viewHeight];
  return { count, width, height, gap, sofaWidth, columns, rows, spanWidth, spanHeight, scale, positions, viewBox, sofaLeft, sofaRight, centre, measureY: 940, labelY: 973 };
}

export function chooseArtworks(catalogue, collection, count, preferred = []) {
  const pool = catalogue.filter(art => art.collection === collection);
  const keys = [...new Set(preferred)].filter(key => pool.some(art => art.key === key));
  for (const art of pool) if (!keys.includes(art.key)) keys.push(art.key);
  return keys.slice(0, Math.min(Math.max(1, Number(count)), 4, pool.length));
}

export function quoteArtworks(artworks, format, width, height, fallbackPrice) {
  const prefix = format === 'canvas' ? 'Stretched canvas' : 'Premium matte poster';
  const lines = artworks.map(art => {
    const variant = art.variants.find(item => {
      const numbers = item.title.match(/\d+/g);
      return item.title.startsWith(prefix) && Number(numbers?.[0]) === Number(width) && Number(numbers?.[1]) === Number(height);
    });
    return {
      key: art.key,
      title: art.title,
      cents: variant ? Number(variant.price) : Number(fallbackPrice),
      variantId: variant?.id ? String(variant.id) : null,
      available: Boolean(variant?.id && variant.available)
    };
  });
  return { lines, total: lines.reduce((sum, line) => sum + line.cents, 0), canPurchase: lines.length > 0 && lines.every(line => line.available) };
}

export function cartItems(quote) {
  if (!quote.canPurchase) throw new Error('Unavailable selection');
  return quote.lines.map(line => ({ id: line.variantId, quantity: 1 }));
}

const collectionRooms = {
  'first-thaw': 'white',
  'lake-country': 'stone',
  'maple-canopy': 'stone',
  'northern-frost': 'white',
  'northern-elements': 'ink'
};

export function roomMode(collection, mode = 'art') {
  if (mode === 'art') return collectionRooms[collection] || 'white';
  return ['white', 'stone', 'ink'].includes(mode) ? mode : 'white';
}

if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined') {
  class NorthernHuesRoom extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.copy = JSON.parse(this.querySelector('[data-room-copy]').textContent);
      this.catalogue = Array.from(this.querySelector('[data-room-catalogue]').options).map(option => ({
        key: option.value,
        title: option.dataset.title,
        collection: option.dataset.collection,
        collectionTitle: option.dataset.collectionTitle,
        image: option.dataset.image,
        variants: JSON.parse(option.dataset.variants || '[]')
      }));
      this.collection = this.querySelector('[data-room-collection]');
      this.count = this.querySelector('[data-room-count]');
      this.layout = this.querySelector('[data-room-layout]');
      this.format = this.querySelector('[data-room-format]');
      this.sizes = this.querySelector('[data-room-size]');
      this.sofa = this.querySelector('[data-room-width]');
      this.gap = this.querySelector('[data-room-gap]');
      this.palette = this.querySelector('[data-room-palette]');
      this.artSelects = Array.from(this.querySelectorAll('[data-room-artwork]'));
      this.shopButton = this.querySelector('[data-room-add-set]');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-CA', { style: 'currency', currency: this.dataset.currency, currencyDisplay: 'code' });
      this.selected = [];
      this.formatSizes(2);
      this.refreshSelection();
      this.addEventListener('change', event => {
        if (event.target === this.format) this.formatSizes(this.sizes.selectedIndex);
        if (event.target === this.collection) this.selected = [];
        if (event.target === this.count && Number(this.count.value) === 4) this.layout.value = 'grid';
        const slot = this.artSelects.indexOf(event.target);
        if (slot !== -1) this.selected[slot] = event.target.value;
        if (event.target === this.sofa) this.sofa.value = String(clamp(this.sofa.value, 60, 120, 84));
        if (event.target === this.gap) this.gap.value = String(clamp(this.gap.value, 1, 8, 3));
        this.refreshSelection();
        this.update();
      }, options);
      for (const input of [this.sofa, this.gap]) input.addEventListener('input', () => { if (input.checkValidity()) this.update(); }, options);
      document.addEventListener('nh:visualize', event => this.selectKeys([event.detail.key], 1), options);
      document.addEventListener('nh:visualize-set', event => this.selectKeys(event.detail.keys, event.detail.count), options);
      this.shopButton.addEventListener('click', () => this.addSet(), options);
      this.update();
      const parameters = new URLSearchParams(window.location.search);
      const requestedKeys = parameters.get('nh_set')?.split(',').map(key => key.trim()).filter(Boolean);
      if (requestedKeys?.length) this.selectKeys(requestedKeys, parameters.get('nh_count') || requestedKeys.length);
    }

    formatSizes(index) {
      const template = this.querySelector(`[data-room-options="${this.format.value}"]`);
      this.sizes.replaceChildren(template.content.cloneNode(true));
      this.sizes.selectedIndex = Math.max(0, Math.min(3, index));
    }

    refreshSelection() {
      this.selected = chooseArtworks(this.catalogue, this.collection.value, Number(this.count.value), this.selected);
      const pool = this.catalogue.filter(art => art.collection === this.collection.value);
      this.artSelects.forEach((select, index) => {
        const active = index < this.selected.length;
        select.closest('[data-room-slot]').hidden = !active;
        select.disabled = !active;
        select.replaceChildren(...pool.map(art => {
          const option = document.createElement('option');
          option.value = art.key;
          option.textContent = art.title;
          option.disabled = this.selected.some((key, slot) => key === art.key && slot !== index);
          return option;
        }));
        if (active) select.value = this.selected[index];
      });
      this.layout.disabled = this.selected.length !== 4;
      this.layout.closest('[data-room-layout-field]').hidden = this.selected.length !== 4;
      this.gap.disabled = this.selected.length === 1;
    }

    selectKeys(keys, requestedCount) {
      const first = this.catalogue.find(art => art.key === keys?.[0]);
      if (!first) return;
      const count = Math.round(clamp(requestedCount || keys.length || 2, 1, 4, 2));
      this.collection.value = first.collection;
      this.count.value = String(count);
      this.selected = keys;
      this.layout.value = count === 4 ? 'grid' : 'row';
      this.refreshSelection();
      this.update();
      this.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      this.artSelects[0].focus({ preventScroll: true });
    }

    fill(template, values) {
      return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)).replaceAll(`__${key}__`, String(value)), template);
    }

    update() {
      const artworks = this.selected.map(key => this.catalogue.find(art => art.key === key)).filter(Boolean);
      const size = this.sizes.selectedOptions[0];
      if (!artworks.length || !size) return;
      const geometry = arrangement({ count: artworks.length, width: size.dataset.width, height: size.dataset.height, gap: this.gap.value, layout: this.layout.value, sofaWidth: this.sofa.value });
      const scene = this.querySelector('[data-room-scene]');
      scene.setAttribute('viewBox', geometry.viewBox.join(' '));
      const mode = roomMode(artworks[0].collection, this.palette?.value || 'art');
      const photo = this.querySelector('[data-room-background]');
      const photoKey = `roomPhoto${mode[0].toUpperCase()}${mode.slice(1)}`;
      const photoUrl = this.dataset[photoKey];
      if (photoUrl && photo.getAttribute('href') !== photoUrl) photo.setAttribute('href', photoUrl);
      const layer = this.querySelector('[data-room-art-layer]');
      layer.replaceChildren(...artworks.map((art, index) => {
        const image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
        image.setAttribute('href', art.image);
        image.setAttribute('preserveAspectRatio', 'xMidYMid slice');
        image.setAttribute('data-room-art', art.key);
        image.setAttribute('aria-label', art.title);
        image.setAttribute('filter', this.format.value === 'canvas' ? this.dataset.canvasShadow : this.dataset.artShadow);
        for (const [key, value] of Object.entries(geometry.positions[index])) image.setAttribute(key, value);
        return image;
      }));
      const set = (selector, attributes) => Object.entries(attributes).forEach(([key, value]) => this.querySelector(selector).setAttribute(key, value));
      const { sofaLeft, sofaRight, measureY, labelY, centre, viewBox } = geometry;
      const markerScale = viewBox[2] / 815;
      set('[data-room-measure]', { d: `M${sofaLeft} ${measureY - 4}V${measureY + 4}M${sofaLeft} ${measureY}H${sofaRight}M${sofaRight} ${measureY - 4}V${measureY + 4}`, 'stroke-width': 1.2 * markerScale });
      set('[data-room-sofa-label]', { x: centre, y: labelY, 'font-size': 16 * markerScale });
      const sofaCaption = this.fill(this.dataset.sofaCaption, { width: geometry.sofaWidth, cm: Math.round(geometry.sofaWidth * 2.54) });
      const caption = this.fill(artworks.length === 1 ? this.copy.arrangementOne : this.copy.arrangementMany, { count: artworks.length, width: geometry.width, height: geometry.height });
      this.querySelector('[data-room-sofa-label]').textContent = sofaCaption;
      this.querySelector('[data-room-caption]').textContent = `${artworks.map(art => art.title).join(' + ')} · ${caption}`;
      this.querySelector('[data-room-description]').textContent = `${caption}. ${artworks.map(art => art.title).join(', ')}. ${sofaCaption}.`;
      this.querySelector('[data-room-dimensions]').textContent = this.fill(artworks.length === 1 ? this.copy.dimensionsOne : this.copy.dimensionsMany, { width: geometry.spanWidth, height: geometry.spanHeight, gap: geometry.gap });
      this.querySelector('[data-room-wide]').hidden = geometry.spanWidth <= geometry.sofaWidth;
      this.querySelector('[data-room-crop]').hidden = !(geometry.width === 28 && geometry.height === 40);
      const quote = quoteArtworks(artworks, this.format.value, geometry.width, geometry.height, size.dataset.price);
      this.quote = quote;
      const lines = this.querySelector('[data-room-price-lines]');
      lines.replaceChildren(...quote.lines.map(line => {
        const item = document.createElement('li');
        const title = document.createElement('span');
        title.textContent = line.title;
        const price = document.createElement('span');
        price.textContent = this.money.format(line.cents / 100);
        item.append(title, price);
        return item;
      }));
      this.querySelector('[data-room-price]').textContent = this.money.format(quote.total / 100);
      this.shopButton.textContent = this.fill(artworks.length === 1 ? this.copy.addOne : this.copy.addMany, { count: artworks.length });
      this.shopButton.hidden = !quote.canPurchase;
      this.querySelector('[data-room-launch]').hidden = quote.canPurchase;
      this.querySelector('[data-room-launch]').textContent = artworks.some(art => art.variants.length) ? this.copy.unavailable : this.copy.launch;
      this.querySelector('[data-room-status]').textContent = '';
    }

    async addSet() {
      if (!this.quote?.canPurchase || this.busy) return;
      this.busy = true;
      this.shopButton.disabled = true;
      this.shopButton.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(this.dataset.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: cartItems(this.quote) }) });
        if (!response.ok) throw new Error('Cart request failed');
        window.location.assign(this.dataset.cartUrl);
      } catch {
        this.querySelector('[data-room-status]').textContent = this.copy.error;
      } finally {
        this.busy = false;
        this.shopButton.disabled = false;
        this.shopButton.removeAttribute('aria-busy');
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
  }

  class NorthernHuesPairings extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-preview-pair]');
        if (!button) return;
        const keys = button.dataset.previewPair.split(',').map(key => key.trim()).filter(Boolean);
        document.dispatchEvent(new CustomEvent('nh:visualize-set', { detail: { keys, count: keys.length } }));
      }, { signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
  }

  if (!customElements.get('nh-room')) customElements.define('nh-room', NorthernHuesRoom);
  if (!customElements.get('nh-pairings')) customElements.define('nh-pairings', NorthernHuesPairings);
}

